import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

enum ResponseErrorType {
  availabilityError = 'availabilityError',
  lowAvailabilityError = 'lowAvailabilityError',
  pricingError = 'pricingError'
}

type ResponseErrors = Record<keyof typeof ResponseErrorType, string | null>;

const defaultErrors: ResponseErrors = {
  availabilityError: null,
  lowAvailabilityError: null,
  pricingError: null
};

const defaultContext = {
  responseErrors: defaultErrors,
  updateResponseErrors(_error: Partial<ResponseErrors>) {}
};

type ContextType = typeof defaultContext;

const ErrorContext = createContext<ContextType>(defaultContext);

export function ErrorContextProvider({ children }) {
  const [responseErrors, setErrors] = useState<ResponseErrors>(defaultErrors);

  const updateResponseErrors = useCallback(
    (error: Partial<ResponseErrors>) =>
      setErrors(prevErrors => ({ ...prevErrors, ...error })),
    []
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      responseErrors,
      updateResponseErrors
    }),
    [responseErrors, updateResponseErrors]
  );

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
}

ErrorContextProvider.displayName = 'ErrorContextProvider';

export function useResponseErrors(): ResponseErrors {
  const { responseErrors } = useContext(ErrorContext);

  return responseErrors;
}

export default ErrorContext;
